/**
 * phononLatticePipeline — 2D spring-mass network with wave propagation.
 *
 * A grid of masses connected by springs. Each mass has a displacement
 * and velocity. Springs transmit forces. We simulate the wave
 * equation on the lattice using semi-implicit integration.
 *
 * For performance we only do a few steps per frame and use a
 * low-resolution grid.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface PhononLatticePipeline {
  updateProgram: WebGLProgram | null;
  renderProgram: WebGLProgram | null;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  stateTextures: [WebGLTexture | null, WebGLTexture | null];
  stateBuffers: [WebGLFramebuffer | null, WebGLFramebuffer | null];
  pingIdx: number;
  bands: Float32Array;
  gridSize: number;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const UPDATE_FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outState;

uniform sampler2D uPrevState;

void main() {
  // State: (R = displacement, G = velocity, B, A unused).
  vec2 texel = 1.0 / vec2(textureSize(uPrevState, 0));
  vec4 me = texture(uPrevState, vUV);
  float h = me.r;
  float v = me.g;
  // Sum of neighbors' displacements.
  float sum = 0.0;
  sum += texture(uPrevState, vUV + vec2(texel.x, 0.0)).r;
  sum += texture(uPrevState, vUV - vec2(texel.x, 0.0)).r;
  sum += texture(uPrevState, vUV + vec2(0.0, texel.y)).r;
  sum += texture(uPrevState, vUV - vec2(0.0, texel.y)).r;
  // Wave equation: a = c^2 * laplacian.
  // Using c^2 = 0.2 for stability.
  float a = 0.2 * (sum - 4.0 * h);
  // v += a * dt; h += v * dt (leapfrog with dt=1).
  float newV = v + a;
  float newH = h + newV;
  // Damping: 0.9995 keeps the wave from blowing up.
  outState = vec4(newH * 0.9995, newV * 0.9995, 0.0, 1.0);
}
`;

// Vibrant render shader: visualize the phonon field with thermal colors,
// displacement-based glow, and isocontours for cinematic look.
const RENDER_FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform sampler2D uState;
uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;


vec3 thermal(float h) {
  // Range-compressed hot palette: deep purple → magenta → orange → yellow → white.
  float a = h * 4.0 + 0.5;
  return vec3(
    smoothstep(-1.0, 1.5, a),
    smoothstep(0.0, 1.5, a - 0.3) * 0.8,
    smoothstep(0.5, 1.8, a - 0.6) + 0.1
  );
}

void main() {
  float bassAvg = (uBass.x + uBass.y + uBass.z) / 3.0;
  float midAvg = (uMid.x + uMid.y + uMid.z) / 3.0;
  float highAvg = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  vec2 texel = 1.0 / vec2(textureSize(uState, 0));
  vec4 me = texture(uState, vUV);
  float h = me.r;
  // Displacement gradient — sample neighbors.
  float dx = texture(uState, vUV + vec2(texel.x, 0.0)).r - texture(uState, vUV - vec2(texel.x, 0.0)).r;
  float dy = texture(uState, vUV + vec2(0.0, texel.y)).r - texture(uState, vUV - vec2(0.0, texel.y)).r;
  // Voronoi-like contours at multiple levels.
  float v = abs(h);
  float contour1 = smoothstep(0.02, 0.0, abs(fract(v * 4.0) - 0.5) - 0.48);
  float contour2 = smoothstep(0.02, 0.0, abs(fract(v * 8.0) - 0.5) - 0.48);
  // Amplification (tuned for visible but not blown out).
  float hAmp = h * 6.0 + 0.5;
  vec3 base = thermal(hAmp);
  // Bright glow on ridge/zero crossings.
  base += vec3(1.0, 0.85, 0.55) * contour1 * 0.8;
  base += vec3(0.95, 0.40, 0.85) * contour2 * 0.4;
  // Audio pulse — glow brightens with bass.
  base *= 1.0 + bassAvg * 0.5;
  base += vec3(0.40, 0.80, 0.95) * length(vec2(dx, dy)) * 2.0;
  // Vignette.
  vec2 vUV2 = vUV - 0.5;
  base *= 1.0 - length(vUV2) * 0.5;
  outColor = vec4(base, 1.0);
}
`;

function createStateTexture(gl: WebGL2RenderingContext, size: number, seed: number): WebGLTexture {
  const tex = gl.createTexture();
  if (!tex) throw new Error("Failed to create texture");
  gl.bindTexture(gl.TEXTURE_2D, tex);
  const pixels = new Float32Array(size * size * 4);
  let s = seed | 0;
  function rand() {
    s = (s + 0x6D2B79F5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }
  for (let i = 0; i < size * size; i++) {
    // Initial displacement: small random perturbation.
    pixels[i * 4 + 0] = (rand() - 0.5) * 0.5;
    pixels[i * 4 + 1] = 0;
    pixels[i * 4 + 2] = 0;
    pixels[i * 4 + 3] = 1;
  }
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, size, size, 0, gl.RGBA, gl.FLOAT, pixels);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.bindTexture(gl.TEXTURE_2D, null);
  return tex;
}

function attachFbo(gl: WebGL2RenderingContext, tex: WebGLTexture, size: number): WebGLFramebuffer {
  const fb = gl.createFramebuffer();
  if (!fb) throw new Error("Failed to create framebuffer");
  gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
  gl.viewport(0, 0, size, size);
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  return fb;
}

export function createPhononLatticePipeline(gl: WebGL2RenderingContext, size = 96): PhononLatticePipeline | null {
  const updateProgram = linkProgram(gl, VERT_SRC, UPDATE_FRAG_SRC, POST_ATTRIBS);
  const renderProgram = linkProgram(gl, VERT_SRC, RENDER_FRAG_SRC, POST_ATTRIBS);
  if (!updateProgram || !renderProgram) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);

  const tex0 = createStateTexture(gl, size, 999);
  const tex1 = createStateTexture(gl, size, 999);  // Same seed = same initial state
  const fb0 = attachFbo(gl, tex0, size);
  const fb1 = attachFbo(gl, tex1, size);

  return {
    updateProgram, renderProgram, vao, vbo,
    stateTextures: [tex0, tex1],
    stateBuffers: [fb0, fb1],
    pingIdx: 0,
    bands: new Float32Array(9),
    gridSize: size,
  };
}

export function computePhononLatticeBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.4);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.6 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 0.9 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.1 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.3 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.6 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.0 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.4 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 2.9 + 3.0);
}

export function computePhononLatticeRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function drawPhononLattice(
  gl: WebGL2RenderingContext,
  pipe: PhononLatticePipeline,
  width: number,
  height: number,
  t: number,
): void {
  if (!pipe.updateProgram || !pipe.renderProgram) return;
  const size = pipe.gridSize;
  // Run several update steps.
  const STEPS = 8;
  for (let i = 0; i < STEPS; i++) {
    const srcIdx = pipe.pingIdx;
    const dstIdx = 1 - pipe.pingIdx;
    const srcTex = pipe.stateTextures[srcIdx];
    const dstFb = pipe.stateBuffers[dstIdx];
    if (!srcTex || !dstFb) continue;
    gl.bindFramebuffer(gl.FRAMEBUFFER, dstFb);
    gl.viewport(0, 0, size, size);
    gl.useProgram(pipe.updateProgram);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, srcTex);
    const uPrev = gl.getUniformLocation(pipe.updateProgram, "uPrevState");
    if (uPrev) gl.uniform1i(uPrev, 0);
    gl.bindVertexArray(pipe.vao);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.bindVertexArray(null);
    pipe.pingIdx = dstIdx;
  }

  // Render.
  const finalTex = pipe.stateTextures[pipe.pingIdx];
  if (!finalTex) return;
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.viewport(0, 0, width, height);
  gl.clearColor(0, 0, 0, 1);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.useProgram(pipe.renderProgram);
  const uState = gl.getUniformLocation(pipe.renderProgram, "uState");
  if (uState) gl.uniform1i(uState, 0);
  const uResolution = gl.getUniformLocation(pipe.renderProgram, "uResolution");
  if (uResolution) gl.uniform2f(uResolution, width, height);
  const uTime = gl.getUniformLocation(pipe.renderProgram, "uTime");
  if (uTime) gl.uniform1f(uTime, t);
  const uBass = gl.getUniformLocation(pipe.renderProgram, "uBass");
  if (uBass) gl.uniform3f(uBass, pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  const uMid = gl.getUniformLocation(pipe.renderProgram, "uMid");
  if (uMid) gl.uniform3f(uMid, pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  const uHigh = gl.getUniformLocation(pipe.renderProgram, "uHigh");
  if (uHigh) gl.uniform3f(uHigh, pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, finalTex);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
